import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FaNetworkWired,
  FaCalculator,
  FaTerminal,
  FaGraduationCap,
  FaTable,
  FaCopy,
  FaCheck,
  FaRedo,
  FaPlus,
  FaTrash,
  FaTools,
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle
} from 'react-icons/fa';

import {
  calculateSubnet,
  calculateVLSM,
  generateCIDRMatrix,
  cidrToMask
} from '../utils/subnetCalculator';

import {
  generateCiscoInterfaceConfig,
  generateCiscoOspfConfig,
  generateCiscoAclConfig,
  generateCiscoDhcpConfig,
  generateCiscoStaticRouteConfig
} from '../utils/ciscoConfigGenerator';

const CiscoSubnetLabPage = () => {
  const [activeTab, setActiveTab] = useState('cidr');

  // CIDR Inspector State
  const [ipInput, setIpInput] = useState('192.168.10.25');
  const [cidrPrefix, setCidrPrefix] = useState(24);

  // VLSM State
  const [majorIp, setMajorIp] = useState('192.168.0.0');
  const [majorCidr, setMajorCidr] = useState(24);
  const [vlsmRequirements, setVlsmRequirements] = useState([
    { id: '1', name: 'HQ Engineering LAN', neededHosts: 60 },
    { id: '2', name: 'Sales & Marketing VLAN', neededHosts: 28 },
    { id: '3', name: 'VoIP Infrastructure', neededHosts: 14 },
    { id: '4', name: 'Server Management SVI', neededHosts: 6 },
    { id: '5', name: 'WAN Point-to-Point Link', neededHosts: 2 },
  ]);

  // Cisco CLI State
  const [cliScriptType, setCliScriptType] = useState('interface');
  const [copiedCode, setCopiedCode] = useState(false);

  // Quiz State
  const [quizQuestion, setQuizQuestion] = useState(() => generateRandomQuestion());
  const [userAnswer, setUserAnswer] = useState('');
  const [quizFeedback, setQuizFeedback] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  // Matrix Filter State
  const [matrixSearch, setMatrixSearch] = useState('');

  // Subnet calculation memo
  const subnetDetails = useMemo(() => {
    return calculateSubnet(ipInput, Number(cidrPrefix));
  }, [ipInput, cidrPrefix]);

  // VLSM calculation memo
  const vlsmResult = useMemo(() => {
    return calculateVLSM(majorIp, Number(majorCidr), vlsmRequirements);
  }, [majorIp, majorCidr, vlsmRequirements]);

  // CIDR Matrix memo
  const cidrMatrix = useMemo(() => generateCIDRMatrix(), []);

  // Filtered matrix
  const filteredMatrix = useMemo(() => {
    if (!matrixSearch.trim()) return cidrMatrix;
    const term = matrixSearch.toLowerCase();
    return cidrMatrix.filter(
      row =>
        row.cidr.toLowerCase().includes(term) ||
        row.mask.toLowerCase().includes(term) ||
        row.wildcard.toLowerCase().includes(term) ||
        row.usableHosts.toLowerCase().includes(term)
    );
  }, [cidrMatrix, matrixSearch]);

  // CLI Code Generator memo
  const generatedCliCode = useMemo(() => {
    if (!subnetDetails.valid) return '! Invalid subnet parameters entered';
    switch (cliScriptType) {
      case 'interface':
        return generateCiscoInterfaceConfig(subnetDetails);
      case 'ospf':
        return generateCiscoOspfConfig(subnetDetails);
      case 'acl':
        return generateCiscoAclConfig(subnetDetails);
      case 'dhcp':
        return generateCiscoDhcpConfig(subnetDetails);
      case 'static':
        return generateCiscoStaticRouteConfig(subnetDetails);
      default:
        return '';
    }
  }, [subnetDetails, cliScriptType]);

  // Helper: Copy CLI snippet
  const handleCopyCli = () => {
    navigator.clipboard.writeText(generatedCliCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2200);
  };

  // Helper: Add VLSM requirement
  const handleAddVlsmRequirement = () => {
    const nextId = String(Date.now());
    setVlsmRequirements([
      ...vlsmRequirements,
      { id: nextId, name: `Department VLAN ${vlsmRequirements.length + 1}`, neededHosts: 10 }
    ]);
  };

  // Helper: Remove VLSM requirement
  const handleRemoveVlsmRequirement = (id) => {
    if (vlsmRequirements.length <= 1) return;
    setVlsmRequirements(vlsmRequirements.filter(r => r.id !== id));
  };

  // Helper: Update VLSM requirement
  const handleUpdateVlsmRequirement = (id, field, value) => {
    setVlsmRequirements(
      vlsmRequirements.map(r => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  // Generate CCNA Subnet Quiz Question
  function generateRandomQuestion() {
    const sampleOctet1 = [10, 172, 192][Math.floor(Math.random() * 3)];
    let sampleOctet2 = 0;
    if (sampleOctet1 === 10) sampleOctet2 = Math.floor(Math.random() * 255);
    else if (sampleOctet1 === 172) sampleOctet2 = Math.floor(Math.random() * 16) + 16;
    else sampleOctet2 = 168;

    const sampleOctet3 = Math.floor(Math.random() * 254) + 1;
    const sampleOctet4 = Math.floor(Math.random() * 254) + 1;
    const prefix = [22, 24, 25, 26, 27, 28, 29, 30][Math.floor(Math.random() * 8)];
    const ip = `${sampleOctet1}.${sampleOctet2}.${sampleOctet3}.${sampleOctet4}`;

    const calculated = calculateSubnet(ip, prefix);

    const questionTypes = ['network', 'broadcast', 'wildcard', 'mask', 'usable'];
    const chosenType = questionTypes[Math.floor(Math.random() * questionTypes.length)];

    let questionText = '';
    let correctAnswer = '';
    let explanation = '';

    if (chosenType === 'network') {
      questionText = `What is the Network Address for ${ip}/${prefix}?`;
      correctAnswer = calculated.networkAddress;
      explanation = `Applying mask ${calculated.subnetMask} to ${ip} zeroes out host bits, producing Network ID ${calculated.networkAddress}.`;
    } else if (chosenType === 'broadcast') {
      questionText = `What is the Broadcast Address for ${ip}/${prefix}?`;
      correctAnswer = calculated.broadcastAddress;
      explanation = `Bitwise OR of Network ID ${calculated.networkAddress} with Wildcard ${calculated.wildcardMask} produces Broadcast ${calculated.broadcastAddress}.`;
    } else if (chosenType === 'wildcard') {
      questionText = `What is the Cisco Inverted Wildcard Mask for prefix /${prefix}?`;
      correctAnswer = calculated.wildcardMask;
      explanation = `Inverting the subnet mask (${calculated.subnetMask}) gives Cisco wildcard mask ${calculated.wildcardMask}.`;
    } else if (chosenType === 'mask') {
      questionText = `What is the standard Dotted Decimal Subnet Mask for /${prefix}?`;
      correctAnswer = calculated.subnetMask;
      explanation = `A /${prefix} prefix has ${prefix} continuous 1s in binary, yielding ${calculated.subnetMask}.`;
    } else {
      questionText = `How many usable host addresses can be assigned in a /${prefix} subnet?`;
      correctAnswer = String(calculated.usableHosts);
      explanation = `Formula: 2^(32 - ${prefix}) - 2 = 2^${32 - prefix} - 2 = ${calculated.usableHosts} usable hosts.`;
    }

    return { ip, prefix, questionText, correctAnswer, explanation };
  }

  const handleCheckQuiz = (e) => {
    e.preventDefault();
    if (!userAnswer.trim()) return;

    const isCorrect = userAnswer.trim().toLowerCase() === quizQuestion.correctAnswer.toLowerCase();
    setQuizFeedback({
      isCorrect,
      explanation: quizQuestion.explanation,
      correctAnswer: quizQuestion.correctAnswer
    });

    setScore(prev => ({
      correct: isCorrect ? prev.correct + 1 : prev.correct,
      total: prev.total + 1
    }));
  };

  const handleNextQuizQuestion = () => {
    setQuizQuestion(generateRandomQuestion());
    setUserAnswer('');
    setQuizFeedback(null);
  };

  return (
    <div style={{
      minHeight: '92vh',
      backgroundColor: '#040d1a',
      color: '#e2e8f0',
      padding: '36px 20px 80px 20px',
      background: 'radial-gradient(ellipse at top, #0b1e36 0%, #040d1a 75%)'
    }}>
      <div style={{ maxWidth: '1240px', margin: '0 auto' }}>

        {/* Top Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', color: '#45aaf2', fontSize: '0.88rem', fontWeight: 600 }}>
          <FaTools />
          <Link to="/tools" style={{ color: '#94a3b8', textDecoration: 'none' }}>Tools Hub</Link>
          <span style={{ color: '#64748b' }}>/</span>
          <span>Cisco CIDR &amp; Subnetting Engineering Lab</span>
        </div>

        {/* Header Hero */}
        <div style={{
          backgroundColor: 'rgba(11, 28, 52, 0.75)',
          border: '1px solid rgba(69, 170, 242, 0.3)',
          borderRadius: '16px',
          padding: '28px 32px',
          backdropFilter: 'blur(12px)',
          marginBottom: '28px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(69, 170, 242, 0.15)', border: '1px solid rgba(69, 170, 242, 0.35)', padding: '4px 12px', borderRadius: '20px', fontSize: '0.78rem', color: '#45aaf2', fontWeight: 700, marginBottom: '8px' }}>
              <FaNetworkWired /> Cisco Certified Enterprise Network Engineering Lab
            </div>
            <h1 style={{ fontSize: '2.1rem', color: '#ffffff', margin: '0 0 8px 0', fontWeight: 700, letterSpacing: '-0.5px' }}>
              Cisco CIDR &amp; Subnetting Engineering Lab
            </h1>
            <p style={{ fontSize: '0.95rem', color: '#94a3b8', margin: 0, maxWidth: '780px', lineHeight: '1.5' }}>
              Precision bitwise IPv4/IPv6 subnet calculation, dynamic Variable Length Subnet Masking (VLSM) hierarchical allocation, Cisco IOS CLI generation (Interface, OSPF, ACLs, DHCP), and interactive CCNA certification drill practice.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <span style={{
              background: 'rgba(46, 204, 113, 0.15)',
              border: '1px solid rgba(46, 204, 113, 0.3)',
              color: '#2ecc71',
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '0.82rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              ● CCNA / CCNP Aligned
            </span>
            <span style={{
              background: 'rgba(69, 170, 242, 0.12)',
              border: '1px solid rgba(69, 170, 242, 0.25)',
              color: '#45aaf2',
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '0.82rem',
              fontWeight: 600
            }}>
              RFC 1918 Verified
            </span>
          </div>
        </div>

        {/* Tab Navigation Bar */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          borderBottom: '1px solid rgba(69, 170, 242, 0.2)',
          paddingBottom: '12px',
          overflowX: 'auto'
        }}>
          {[
            { id: 'cidr', label: 'CIDR Inspector & Bitwise', icon: <FaCalculator /> },
            { id: 'vlsm', label: 'Dynamic VLSM Planner', icon: <FaNetworkWired /> },
            { id: 'cisco-cli', label: 'Cisco IOS CLI Generator', icon: <FaTerminal /> },
            { id: 'quiz', label: 'CCNA Practice & Drill', icon: <FaGraduationCap /> },
            { id: 'matrix', label: 'CIDR Reference Matrix', icon: <FaTable /> }
          ].map(tab => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 18px',
                  borderRadius: '10px',
                  border: isActive ? '1px solid #45aaf2' : '1px solid transparent',
                  backgroundColor: isActive ? 'rgba(69, 170, 242, 0.18)' : 'rgba(255, 255, 255, 0.03)',
                  color: isActive ? '#ffffff' : '#94a3b8',
                  fontWeight: isActive ? 700 : 500,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                <span style={{ color: isActive ? '#45aaf2' : '#64748b' }}>{tab.icon}</span>
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* TAB 1: CIDR INSPECTOR & BITWISE ANALYZER */}
        {activeTab === 'cidr' && (
          <div>
            {/* Input & Slider Card */}
            <div style={{
              backgroundColor: 'rgba(8, 20, 39, 0.85)',
              border: '1px solid rgba(69, 170, 242, 0.25)',
              borderRadius: '14px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', alignItems: 'center' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', color: '#94a3b8', fontWeight: 600, marginBottom: '8px' }}>
                    Target IPv4 Address:
                  </label>
                  <input
                    type="text"
                    value={ipInput}
                    onChange={(e) => setIpInput(e.target.value)}
                    placeholder="e.g. 192.168.1.50"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(5, 15, 29, 0.8)',
                      border: subnetDetails.valid ? '1px solid rgba(69, 170, 242, 0.4)' : '1px solid #ff4757',
                      color: '#ffffff',
                      fontSize: '1.05rem',
                      fontFamily: 'monospace',
                      boxSizing: 'border-box'
                    }}
                  />
                  {!subnetDetails.valid && (
                    <div style={{ color: '#ff4757', fontSize: '0.8rem', marginTop: '6px' }}>
                      Please enter a valid IPv4 address (0-255 per octet).
                    </div>
                  )}
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <label style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 600 }}>
                      CIDR Prefix Length: <strong style={{ color: '#45aaf2' }}>/{cidrPrefix}</strong>
                    </label>
                    <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                      Mask: {cidrToMask(Number(cidrPrefix))}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="8"
                    max="32"
                    value={cidrPrefix}
                    onChange={(e) => setCidrPrefix(Number(e.target.value))}
                    style={{ width: '100%', accentColor: '#45aaf2', cursor: 'pointer' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
                    <span>/8 (Class A)</span>
                    <span>/16 (Class B)</span>
                    <span>/24 (Class C)</span>
                    <span>/30 (P2P)</span>
                    <span>/32 (Host)</span>
                  </div>
                </div>
              </div>

              {/* Quick Preset Buttons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '18px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Presets:</span>
                {[
                  { label: '192.168.1.1 /24 (Home/SOHO)', ip: '192.168.1.1', cidr: 24 },
                  { label: '10.0.0.1 /16 (Enterprise Corp)', ip: '10.0.0.1', cidr: 16 },
                  { label: '172.16.0.1 /20 (Cloud VPC)', ip: '172.16.0.1', cidr: 20 },
                  { label: '192.168.50.1 /28 (DMZ Subnet)', ip: '192.168.50.1', cidr: 28 },
                  { label: '10.255.0.1 /30 (WAN Serial Link)', ip: '10.255.0.1', cidr: 30 }
                ].map((preset, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setIpInput(preset.ip);
                      setCidrPrefix(preset.cidr);
                    }}
                    style={{
                      padding: '5px 12px',
                      borderRadius: '6px',
                      border: '1px solid rgba(69, 170, 242, 0.25)',
                      backgroundColor: 'rgba(69, 170, 242, 0.08)',
                      color: '#cbd5e1',
                      fontSize: '0.78rem',
                      cursor: 'pointer'
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Subnet KPI Cards Grid */}
            {subnetDetails.valid && (
              <>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{ backgroundColor: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(69, 170, 242, 0.3)', borderRadius: '12px', padding: '16px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Network ID</div>
                    <div style={{ fontSize: '1.25rem', color: '#ffffff', fontWeight: 700, fontFamily: 'monospace', marginTop: '4px' }}>
                      {subnetDetails.networkAddress}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#45aaf2', marginTop: '4px' }}>Prefix /{subnetDetails.cidr}</div>
                  </div>

                  <div style={{ backgroundColor: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(69, 170, 242, 0.3)', borderRadius: '12px', padding: '16px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Broadcast Address</div>
                    <div style={{ fontSize: '1.25rem', color: '#ffffff', fontWeight: 700, fontFamily: 'monospace', marginTop: '4px' }}>
                      {subnetDetails.broadcastAddress}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#f8d210', marginTop: '4px' }}>All Host Bits Set (1s)</div>
                  </div>

                  <div style={{ backgroundColor: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(69, 170, 242, 0.3)', borderRadius: '12px', padding: '16px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Subnet Mask</div>
                    <div style={{ fontSize: '1.25rem', color: '#ffffff', fontWeight: 700, fontFamily: 'monospace', marginTop: '4px' }}>
                      {subnetDetails.subnetMask}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#2ecc71', marginTop: '4px' }}>Hex: {subnetDetails.hexMask}</div>
                  </div>

                  <div style={{ backgroundColor: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(69, 170, 242, 0.3)', borderRadius: '12px', padding: '16px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Cisco Wildcard Mask</div>
                    <div style={{ fontSize: '1.25rem', color: '#ffffff', fontWeight: 700, fontFamily: 'monospace', marginTop: '4px' }}>
                      {subnetDetails.wildcardMask}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#45aaf2', marginTop: '4px' }}>Inverted Mask (~Subnet)</div>
                  </div>

                  <div style={{ backgroundColor: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(69, 170, 242, 0.3)', borderRadius: '12px', padding: '16px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Usable Host Count</div>
                    <div style={{ fontSize: '1.25rem', color: '#2ecc71', fontWeight: 700, fontFamily: 'monospace', marginTop: '4px' }}>
                      {subnetDetails.usableHosts.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>Total: {subnetDetails.totalAddresses.toLocaleString()} IPs</div>
                  </div>

                  <div style={{ backgroundColor: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(69, 170, 242, 0.3)', borderRadius: '12px', padding: '16px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Classification</div>
                    <div style={{ fontSize: '1.05rem', color: '#ffffff', fontWeight: 700, marginTop: '4px' }}>
                      {subnetDetails.ipClass}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#45aaf2', marginTop: '4px' }}>{subnetDetails.ipType}</div>
                  </div>
                </div>

                {/* Usable IP Range Box */}
                <div style={{
                  backgroundColor: 'rgba(15, 39, 68, 0.6)',
                  border: '1px solid rgba(69, 170, 242, 0.3)',
                  borderRadius: '12px',
                  padding: '20px 24px',
                  marginBottom: '24px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '16px'
                }}>
                  <div>
                    <span style={{ fontSize: '0.78rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>
                      Assignable Usable Host Range:
                    </span>
                    <div style={{ fontSize: '1.3rem', color: '#ffffff', fontWeight: 700, fontFamily: 'monospace', marginTop: '4px' }}>
                      {subnetDetails.firstUsable} <span style={{ color: '#45aaf2' }}>—</span> {subnetDetails.lastUsable}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '16px' }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Network Bits (1s)</div>
                      <div style={{ fontSize: '1.1rem', color: '#45aaf2', fontWeight: 700 }}>{subnetDetails.networkBits} bits</div>
                    </div>
                    <div style={{ borderLeft: '1px solid rgba(255, 255, 255, 0.1)', paddingLeft: '16px', textAlign: 'right' }}>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Host Bits (0s)</div>
                      <div style={{ fontSize: '1.1rem', color: '#2ecc71', fontWeight: 700 }}>{subnetDetails.hostBits} bits</div>
                    </div>
                  </div>
                </div>

                {/* 32-Bit Binary Bitwise Breakdown Representation */}
                <div style={{
                  backgroundColor: 'rgba(8, 20, 39, 0.9)',
                  border: '1px solid rgba(69, 170, 242, 0.25)',
                  borderRadius: '14px',
                  padding: '24px',
                  marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#ffffff' }}>
                        32-Bit Binary Bitwise Breakdown
                      </h3>
                      <p style={{ margin: '4px 0 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>
                        Visual distribution of 32 bits across the 4 octets. Network bits determine the subnet boundary; host bits provide host addressing.
                      </p>
                    </div>

                    <div style={{ display: 'flex', gap: '16px', fontSize: '0.8rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: '#45aaf2' }}></span>
                        <span>Network Bits ({subnetDetails.networkBits})</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: '#2ecc71' }}></span>
                        <span>Host Bits ({subnetDetails.hostBits})</span>
                      </div>
                    </div>
                  </div>

                  {/* Octets visual blocks */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: '16px'
                  }}>
                    {subnetDetails.binaryOctets.map((octet, octetIdx) => {
                      const decimalVal = ipInput.split('.')[octetIdx] || 0;
                      return (
                        <div
                          key={octetIdx}
                          style={{
                            backgroundColor: 'rgba(5, 15, 29, 0.8)',
                            border: '1px solid rgba(69, 170, 242, 0.2)',
                            borderRadius: '10px',
                            padding: '12px 14px'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '8px' }}>
                            <span>Octet {octetIdx + 1}</span>
                            <span style={{ color: '#ffffff', fontFamily: 'monospace', fontWeight: 700 }}>Decimal: {decimalVal}</span>
                          </div>

                          {/* Bits row */}
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            {octet.map((bObj, bIdx) => (
                              <div
                                key={bIdx}
                                style={{
                                  flex: 1,
                                  textAlign: 'center',
                                  padding: '8px 0',
                                  borderRadius: '4px',
                                  fontSize: '0.95rem',
                                  fontFamily: 'monospace',
                                  fontWeight: 700,
                                  backgroundColor: bObj.isNetwork ? 'rgba(69, 170, 242, 0.2)' : 'rgba(46, 204, 113, 0.15)',
                                  border: `1px solid ${bObj.isNetwork ? '#45aaf2' : '#2ecc71'}`,
                                  color: bObj.isNetwork ? '#45aaf2' : '#2ecc71'
                                }}
                              >
                                {bObj.bit}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* TAB 2: DYNAMIC VLSM PLANNER */}
        {activeTab === 'vlsm' && (
          <div>
            {/* Major Network Settings */}
            <div style={{
              backgroundColor: 'rgba(8, 20, 39, 0.85)',
              border: '1px solid rgba(69, 170, 242, 0.25)',
              borderRadius: '14px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: '0 0 14px 0', color: '#ffffff', fontSize: '1.15rem' }}>
                Major Network Allocation Block
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', alignItems: 'center' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', color: '#94a3b8', fontWeight: 600, marginBottom: '6px' }}>
                    Major Network IP Address:
                  </label>
                  <input
                    type="text"
                    value={majorIp}
                    onChange={(e) => setMajorIp(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(5, 15, 29, 0.8)',
                      border: '1px solid rgba(69, 170, 242, 0.3)',
                      color: '#ffffff',
                      fontSize: '1rem',
                      fontFamily: 'monospace',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', color: '#94a3b8', fontWeight: 600, marginBottom: '6px' }}>
                    Major Network CIDR: <strong style={{ color: '#45aaf2' }}>/{majorCidr}</strong> ({Math.pow(2, 32 - majorCidr).toLocaleString()} Total IPs)
                  </label>
                  <input
                    type="range"
                    min="16"
                    max="28"
                    value={majorCidr}
                    onChange={(e) => setMajorCidr(Number(e.target.value))}
                    style={{ width: '100%', accentColor: '#45aaf2' }}
                  />
                </div>
              </div>

              {/* Capacity Progress Bar */}
              <div style={{ marginTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#94a3b8', marginBottom: '6px' }}>
                  <span>
                    Major Network Utilization: <strong style={{ color: '#ffffff' }}>{vlsmResult.totalAllocatedAddresses}</strong> / {vlsmResult.majorNetwork?.totalAddresses} addresses
                  </span>
                  <span style={{ color: vlsmResult.isOverflow ? '#ff4757' : '#45aaf2', fontWeight: 700 }}>
                    {vlsmResult.utilizationPercent}% {vlsmResult.isOverflow ? '(OVERFLOW)' : 'Used'}
                  </span>
                </div>
                <div style={{ width: '100%', height: '10px', borderRadius: '5px', backgroundColor: 'rgba(255, 255, 255, 0.1)', overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.min(100, vlsmResult.utilizationPercent)}%`,
                    height: '100%',
                    backgroundColor: vlsmResult.isOverflow ? '#ff4757' : '#45aaf2',
                    transition: 'width 0.3s ease'
                  }}></div>
                </div>
              </div>
            </div>

            {/* Subnet Requirements Input Table */}
            <div style={{
              backgroundColor: 'rgba(8, 20, 39, 0.85)',
              border: '1px solid rgba(69, 170, 242, 0.25)',
              borderRadius: '14px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#ffffff' }}>
                    Subnet Requirements &amp; Host Sizing
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>
                    Cisco best practice: VLSM automatically sorts requirements descending to prevent fragmentation and optimize bit alignment.
                  </p>
                </div>

                <button
                  onClick={handleAddVlsmRequirement}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(69, 170, 242, 0.15)',
                    border: '1px solid #45aaf2',
                    color: '#45aaf2',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  <FaPlus size={12} /> Add Subnet
                </button>
              </div>

              {/* Requirements Edit Grid */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {vlsmRequirements.map((req, idx) => (
                  <div
                    key={req.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 140px 40px',
                      gap: '12px',
                      alignItems: 'center',
                      backgroundColor: 'rgba(5, 15, 29, 0.6)',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.08)'
                    }}
                  >
                    <input
                      type="text"
                      value={req.name}
                      onChange={(e) => handleUpdateVlsmRequirement(req.id, 'name', e.target.value)}
                      placeholder="Subnet / VLAN Name"
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(69, 170, 242, 0.25)',
                        color: '#ffffff',
                        fontSize: '0.9rem'
                      }}
                    />

                    <div>
                      <input
                        type="number"
                        min="1"
                        max="65534"
                        value={req.neededHosts}
                        onChange={(e) => handleUpdateVlsmRequirement(req.id, 'neededHosts', e.target.value)}
                        placeholder="Hosts"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          backgroundColor: 'rgba(0, 0, 0, 0.3)',
                          border: '1px solid rgba(69, 170, 242, 0.25)',
                          color: '#2ecc71',
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          fontFamily: 'monospace',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    <button
                      onClick={() => handleRemoveVlsmRequirement(req.id)}
                      disabled={vlsmRequirements.length <= 1}
                      style={{
                        padding: '8px',
                        borderRadius: '6px',
                        border: '1px solid rgba(255, 71, 87, 0.3)',
                        backgroundColor: 'rgba(255, 71, 87, 0.1)',
                        color: '#ff4757',
                        cursor: vlsmRequirements.length <= 1 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FaTrash size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* VLSM Output Hierarchy Table */}
            {vlsmResult.valid && (
              <div style={{
                backgroundColor: 'rgba(8, 20, 39, 0.9)',
                border: '1px solid rgba(69, 170, 242, 0.3)',
                borderRadius: '14px',
                padding: '24px',
                overflowX: 'auto'
              }}>
                <h3 style={{ margin: '0 0 16px 0', color: '#ffffff', fontSize: '1.15rem' }}>
                  Hierarchical VLSM Allocation Plan
                </h3>

                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(69, 170, 242, 0.3)', color: '#94a3b8', textAlign: 'left' }}>
                      <th style={{ padding: '10px' }}>Subnet / VLAN Name</th>
                      <th style={{ padding: '10px' }}>Needed</th>
                      <th style={{ padding: '10px' }}>Allocated</th>
                      <th style={{ padding: '10px' }}>Subnet Network ID</th>
                      <th style={{ padding: '10px' }}>Subnet Mask</th>
                      <th style={{ padding: '10px' }}>Usable Host Range</th>
                      <th style={{ padding: '10px' }}>Broadcast</th>
                      <th style={{ padding: '10px' }}>Efficiency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vlsmResult.subnets.map((sub, idx) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                          backgroundColor: sub.isOverflow ? 'rgba(255, 71, 87, 0.1)' : 'transparent'
                        }}
                      >
                        <td style={{ padding: '12px 10px', color: '#ffffff', fontWeight: 600 }}>
                          {sub.name}
                        </td>
                        <td style={{ padding: '12px 10px', color: '#cbd5e1', fontFamily: 'monospace' }}>
                          {sub.neededHosts}
                        </td>
                        <td style={{ padding: '12px 10px', color: '#2ecc71', fontWeight: 700, fontFamily: 'monospace' }}>
                          {sub.allocatedHosts} (/{sub.cidr})
                        </td>
                        <td style={{ padding: '12px 10px', color: '#45aaf2', fontWeight: 700, fontFamily: 'monospace' }}>
                          {sub.networkAddress}/{sub.cidr}
                        </td>
                        <td style={{ padding: '12px 10px', color: '#cbd5e1', fontFamily: 'monospace' }}>
                          {sub.subnetMask}
                        </td>
                        <td style={{ padding: '12px 10px', color: '#cbd5e1', fontFamily: 'monospace' }}>
                          {sub.firstUsable} — {sub.lastUsable}
                        </td>
                        <td style={{ padding: '12px 10px', color: '#f8d210', fontFamily: 'monospace' }}>
                          {sub.broadcastAddress}
                        </td>
                        <td style={{ padding: '12px 10px', color: '#45aaf2', fontWeight: 600 }}>
                          {sub.efficiency}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 3: CISCO IOS CLI CODE GENERATOR */}
        {activeTab === 'cisco-cli' && (
          <div>
            <div style={{
              backgroundColor: 'rgba(8, 20, 39, 0.85)',
              border: '1px solid rgba(69, 170, 242, 0.25)',
              borderRadius: '14px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', flexWrap: 'wrap', gap: '14px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#ffffff' }}>
                    Cisco IOS Command Generator
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>
                    Generate authentic Cisco syntax tailored for subnet <strong style={{ color: '#45aaf2' }}>{subnetDetails.networkAddress}/{subnetDetails.cidr}</strong>.
                  </p>
                </div>

                {/* Script Selector Tabs */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { id: 'interface', label: 'Interface IP' },
                    { id: 'ospf', label: 'OSPF Routing' },
                    { id: 'acl', label: 'Access Lists (ACL)' },
                    { id: 'dhcp', label: 'DHCP Pool' },
                    { id: 'static', label: 'Static Route' }
                  ].map(item => (
                    <button
                      key={item.id}
                      onClick={() => setCliScriptType(item.id)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '6px',
                        fontSize: '0.82rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        border: cliScriptType === item.id ? '1px solid #45aaf2' : '1px solid rgba(255, 255, 255, 0.1)',
                        backgroundColor: cliScriptType === item.id ? '#45aaf2' : 'rgba(255, 255, 255, 0.04)',
                        color: cliScriptType === item.id ? '#040d1a' : '#cbd5e1'
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Code Terminal Display */}
              <div style={{
                backgroundColor: '#020914',
                border: '1px solid rgba(69, 170, 242, 0.3)',
                borderRadius: '10px',
                padding: '20px',
                position: 'relative'
              }}>
                <button
                  onClick={handleCopyCli}
                  style={{
                    position: 'absolute',
                    top: '14px',
                    right: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    backgroundColor: copiedCode ? '#2ecc71' : 'rgba(69, 170, 242, 0.2)',
                    border: `1px solid ${copiedCode ? '#2ecc71' : 'rgba(69, 170, 242, 0.4)'}`,
                    color: copiedCode ? '#040d1a' : '#45aaf2',
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {copiedCode ? <FaCheck size={12} /> : <FaCopy size={12} />}
                  {copiedCode ? 'Copied to Clipboard' : 'Copy Commands'}
                </button>

                <pre style={{
                  margin: 0,
                  color: '#45aaf2',
                  fontFamily: 'Consolas, Monaco, monospace',
                  fontSize: '0.9rem',
                  lineHeight: '1.6',
                  overflowX: 'auto'
                }}>
                  {generatedCliCode}
                </pre>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: CCNA PRACTICE & DRILL QUIZ */}
        {activeTab === 'quiz' && (
          <div>
            <div style={{
              backgroundColor: 'rgba(8, 20, 39, 0.85)',
              border: '1px solid rgba(69, 170, 242, 0.25)',
              borderRadius: '14px',
              padding: '28px',
              maxWidth: '840px',
              margin: '0 auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaGraduationCap size={22} color="#45aaf2" />
                  <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#ffffff' }}>
                    CCNA Subnetting Mastery Drill
                  </h3>
                </div>

                <div style={{
                  backgroundColor: 'rgba(69, 170, 242, 0.1)',
                  border: '1px solid rgba(69, 170, 242, 0.3)',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '0.85rem',
                  color: '#cbd5e1'
                }}>
                  Score: <strong style={{ color: '#2ecc71' }}>{score.correct}</strong> / {score.total} (
                  {score.total > 0 ? ((score.correct / score.total) * 100).toFixed(0) : 0}%)
                </div>
              </div>

              {/* Question Box */}
              <div style={{
                backgroundColor: 'rgba(5, 15, 29, 0.9)',
                border: '1px solid rgba(69, 170, 242, 0.3)',
                borderRadius: '10px',
                padding: '24px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '0.78rem', color: '#45aaf2', fontWeight: 700, textTransform: 'uppercase' }}>
                  Scenario Question:
                </span>
                <div style={{ fontSize: '1.2rem', color: '#ffffff', fontWeight: 600, marginTop: '8px', lineHeight: '1.5' }}>
                  {quizQuestion.questionText}
                </div>
              </div>

              {/* Input & Form */}
              <form onSubmit={handleCheckQuiz}>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                  <input
                    type="text"
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    placeholder="Enter answer (e.g. 192.168.1.0 or 0.0.0.31 or 62)"
                    disabled={quizFeedback !== null}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(5, 15, 29, 0.8)',
                      border: '1px solid rgba(69, 170, 242, 0.35)',
                      color: '#ffffff',
                      fontSize: '1rem',
                      fontFamily: 'monospace'
                    }}
                  />
                  {!quizFeedback ? (
                    <button
                      type="submit"
                      style={{
                        padding: '12px 24px',
                        borderRadius: '8px',
                        backgroundColor: '#45aaf2',
                        color: '#040d1a',
                        fontWeight: 700,
                        border: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      Submit Answer
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleNextQuizQuestion}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        backgroundColor: '#2ecc71',
                        color: '#040d1a',
                        fontWeight: 700,
                        border: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <FaRedo size={12} /> Next Question
                    </button>
                  )}
                </div>
              </form>

              {/* Feedback Alert */}
              {quizFeedback && (
                <div style={{
                  padding: '16px 20px',
                  borderRadius: '10px',
                  backgroundColor: quizFeedback.isCorrect ? 'rgba(46, 204, 113, 0.15)' : 'rgba(255, 71, 87, 0.15)',
                  border: `1px solid ${quizFeedback.isCorrect ? '#2ecc71' : '#ff4757'}`,
                  marginTop: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '1rem', color: quizFeedback.isCorrect ? '#2ecc71' : '#ff4757', marginBottom: '6px' }}>
                    {quizFeedback.isCorrect ? <FaCheckCircle /> : <FaTimesCircle />}
                    {quizFeedback.isCorrect ? 'Correct! Excellent job.' : `Incorrect. The correct answer is: ${quizFeedback.correctAnswer}`}
                  </div>
                  <div style={{ fontSize: '0.88rem', color: '#cbd5e1', lineHeight: '1.5' }}>
                    {quizFeedback.explanation}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 5: CIDR REFERENCE MATRIX */}
        {activeTab === 'matrix' && (
          <div>
            <div style={{
              backgroundColor: 'rgba(8, 20, 39, 0.85)',
              border: '1px solid rgba(69, 170, 242, 0.25)',
              borderRadius: '14px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#ffffff' }}>
                    IPv4 CIDR Prefix &amp; Mask Quick Lookup Matrix
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>
                    Standard engineering reference for all IPv4 subnet prefixes from /8 through /32.
                  </p>
                </div>

                <div>
                  <input
                    type="text"
                    value={matrixSearch}
                    onChange={(e) => setMatrixSearch(e.target.value)}
                    placeholder="Search prefix, mask or hosts..."
                    style={{
                      padding: '8px 14px',
                      borderRadius: '6px',
                      backgroundColor: 'rgba(5, 15, 29, 0.8)',
                      border: '1px solid rgba(69, 170, 242, 0.3)',
                      color: '#ffffff',
                      fontSize: '0.85rem'
                    }}
                  />
                </div>
              </div>

              {/* Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(69, 170, 242, 0.3)', color: '#94a3b8', textAlign: 'left' }}>
                      <th style={{ padding: '10px' }}>CIDR Prefix</th>
                      <th style={{ padding: '10px' }}>Subnet Mask</th>
                      <th style={{ padding: '10px' }}>Cisco Wildcard Mask</th>
                      <th style={{ padding: '10px' }}>Total Addresses</th>
                      <th style={{ padding: '10px' }}>Usable Hosts</th>
                      <th style={{ padding: '10px' }}>Class C Equivalent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMatrix.map((row, idx) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                          backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.015)' : 'transparent'
                        }}
                      >
                        <td style={{ padding: '10px', color: '#45aaf2', fontWeight: 700, fontFamily: 'monospace' }}>
                          {row.cidr}
                        </td>
                        <td style={{ padding: '10px', color: '#cbd5e1', fontFamily: 'monospace' }}>
                          {row.mask}
                        </td>
                        <td style={{ padding: '10px', color: '#2ecc71', fontFamily: 'monospace' }}>
                          {row.wildcard}
                        </td>
                        <td style={{ padding: '10px', color: '#cbd5e1', fontFamily: 'monospace' }}>
                          {row.totalAddresses}
                        </td>
                        <td style={{ padding: '10px', color: '#ffffff', fontWeight: 700, fontFamily: 'monospace' }}>
                          {row.usableHosts}
                        </td>
                        <td style={{ padding: '10px', color: '#94a3b8' }}>
                          {row.classCEquiv}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Footer Support Info */}
        <div style={{
          marginTop: '32px',
          padding: '16px 20px',
          borderRadius: '10px',
          backgroundColor: 'rgba(69, 170, 242, 0.06)',
          border: '1px solid rgba(69, 170, 242, 0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '0.85rem',
          color: '#94a3b8'
        }}>
          <FaInfoCircle size={18} color="#45aaf2" style={{ flexShrink: 0 }} />
          <span>
            All calculations strictly comply with RFC 791 (IPv4 specification), RFC 1878 (Variable Length Subnet Masking), RFC 1918 (Private Address Allocations), and RFC 3021 (31-Bit Prefixes on Point-to-Point Links).
          </span>
        </div>

      </div>
    </div>
  );
};

export default CiscoSubnetLabPage;
